<template>
    <div class="html-editor">
        <div class="html-toolbar">
            <div class="tool-group">
                <select
                    :value="style"
                    @input="setStyle($event.target.value)">
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="normal">Normal</option>
                </select>
            </div>

            <div class="tool-group">
                <button
                    type="button"
                    :class="{ active: isSelectionBold }"
                    @click="runCommand('toggle-bold')">
                    <i class="fa fa-bold"></i>
                </button>
                <button
                    type="button"
                    :class="{ active: isSelectionItalic }"
                    @click="runCommand('toggle-italic')">
                    <i class="fa fa-italic"></i>
                </button>
            </div>

            <div class="tool-group">
                <button
                    type="button"
                    :class="{ active: isSelectionUl }"
                    @click="runCommand('toggle-ul')">
                    <i class="fa fa-list-ul"></i>
                </button>
                <button
                    type="button"
                    :class="{ active: isSelectionOl }"
                    @click="runCommand('toggle-ol')">
                    <i class="fa fa-list-ol"></i>
                </button>
            </div>

            <div class="tool-group">
                <button
                    type="button"
                    :class="{ active: linkMenuEnabled }"
                    @click="toggleLinkMenu">
                    <i class="fa fa-link"></i>
                </button>
                <button
                    type="button"
                    :class="{ active: imageMenuEnabled }"
                    @click="toggleImageMenu">
                    <i class="fa fa-image"></i>
                </button>
                <button
                    type="button"
                    :class="{ active: videoMenuEnabled }"
                    @click="toggleVideoMenu">
                    <i class="fa fa-video-camera"></i>
                </button>
                <button
                    type="button"
                    :class="{ active: fileMenuEnabled }"
                    @click="toggleFileMenu">
                    <i class="fa fa-file"></i>
                </button>
            </div>

            <div
                class="tool-group"
                v-if="allowEmoji">
                <button
                    type="button"
                    :class="{ active: emojiMenuEnabled }"
                    @click="toggleEmojiMenu">
                    <i class="fa fa-smile-o"></i>
                </button>
            </div>

            <div
                class="toolbar-extension"
                v-if="showToolbarExtension">
                <form
                    class="link-menu"
                    @submit.prevent="isSelectionLink ? removeLink() : createLink()"
                    v-if="linkMenuEnabled">
                    <input
                        class="form-control"
                        type="text"
                        placeholder="URL"
                        :disabled="isSelectionLink"
                        v-model="linkMenuUrl">
                    <input
                        class="form-control"
                        type="text"
                        placeholder="Text (optional)"
                        :disabled="!isSelectionEmpty"
                        v-model="linkMenuText">
                    <input
                        class="btn btn-success"
                        type="submit"
                        value="Remove Link"
                        v-if="isSelectionLink">
                    <input
                        class="btn btn-success"
                        type="submit"
                        value="Add link"
                        :disabled="!canCreateLink"
                        :title="linkMenuStatus"
                        v-else>
                </form>

                <form
                    class="image-menu"
                    @submit.prevent="createImage"
                    v-if="imageMenuEnabled">
                    <div class="mode radio-input">
                        <label :class="{ active: imageMenuMode === 'file' }">
                            File
                            <input
                                type="radio"
                                value="file"
                                checked
                                v-model="imageMenuMode">
                        </label>
                        <label :class="{ active: imageMenuMode === 'url' }">
                            URL
                            <input
                                type="radio"
                                value="url"
                                v-model="imageMenuMode">
                        </label>
                    </div>
                    <label
                        class="file-input"
                        v-if="imageMenuMode === 'file'">
                        <input
                            type="file"
                            multiple="multiple"
                            :accept="allowedImageTypes.join(',')"
                            @change="imageMenuFiles = Array.from($event.target.files)">
                        <div class="action-btn">Choose files</div>
                        <div
                            class="filename"
                            :title="imageMenuFileNames"
                            v-if="imageMenuFiles.length > 0">
                            {{ imageMenuFileNames }}
                        </div>
                        <div
                            class="filename"
                            v-else>
                            No file selected
                        </div>
                    </label>
                    <input
                        class="form-control"
                        type="text"
                        placeholder="URL"
                        v-if="imageMenuMode === 'url'"
                        v-model="imageMenuUrl">
                    <input
                        class="form-control"
                        type="text"
                        placeholder="Caption (optional)"
                        v-model="imageMenuCaption">
                    <input
                        class="btn btn-success"
                        type="submit"
                        value="Add images"
                        :disabled="!canCreateImage"
                        :title="imageMenuStatus">
                </form>

                <form
                    class="video-menu"
                    @submit.prevent="createVideo"
                    v-if="videoMenuEnabled">
                    <div class="mode radio-input">
                        <label :class="{ active: videoMenuMode === 'file' }">
                            File
                            <input
                                type="radio"
                                value="file"
                                checked
                                v-model="videoMenuMode">
                        </label>
                        <label :class="{ active: videoMenuMode === 'url' }">
                            URL
                            <input
                                type="radio"
                                value="url"
                                v-model="videoMenuMode">
                        </label>
                    </div>
                    <label
                        class="file-input"
                        v-if="videoMenuMode === 'file'">
                        <input
                            type="file"
                            multiple="multiple"
                            :accept="allowedVideoTypes.join(',')"
                            @change="videoMenuFiles = Array.from($event.target.files)">
                        <div class="action-btn">Choose files</div>
                        <div
                            class="filename"
                            :title="videoMenuFileNames"
                            v-if="videoMenuFiles.length > 0">
                            {{ videoMenuFileNames }}
                        </div>
                        <div
                            class="filename"
                            v-else>
                            No file selected
                        </div>
                    </label>
                    <input
                        class="form-control"
                        type="text"
                        placeholder="YouTube or Vimeo URL"
                        v-if="videoMenuMode === 'url'"
                        v-model="videoMenuUrl">
                    <input
                        class="form-control"
                        type="text"
                        placeholder="Caption (optional)"
                        v-model="videoMenuCaption">
                    <input
                        class="btn btn-success"
                        type="submit"
                        value="Add videos"
                        :disabled="!canCreateVideo"
                        :title="videoMenuStatus">
                </form>

                <form
                    class="file-menu"
                    @submit.prevent="createFiles"
                    v-if="fileMenuEnabled">
                    <label class="file-input">
                        <input
                            type="file"
                            multiple="multiple"
                            @change="fileMenuFiles = Array.from($event.target.files)">
                        <div class="action-btn">Choose files</div>
                        <div
                            class="filename"
                            :title="fileMenuFileNames"
                            v-if="fileMenuFiles.length > 0">
                            {{ fileMenuFileNames }}
                        </div>
                        <div
                            class="filename"
                            v-else>
                            No file selected
                        </div>
                    </label>
                    <input
                        class="btn btn-success"
                        type="submit"
                        value="Add files"
                        :disabled="!canCreateFiles"
                        :title="fileMenuStatus">
                </form>

                <EmojiPicker
                    ref="emojiPicker"
                    @pick="createEmoji($event)"
                    v-if="emojiMenuEnabled">
                </EmojiPicker>
            </div>
        </div>
    </div>
</template>

<script>
    import * as State from 'prosemirror-state';
    import * as View from 'prosemirror-view';
    import * as Model from 'prosemirror-model';
    import * as BasicSchema from 'prosemirror-schema-basic';
    import * as ListSchema from 'prosemirror-schema-list';
    import * as InputRules from 'prosemirror-inputrules';
    import * as Keymap from 'prosemirror-keymap';
    import * as Commands from 'prosemirror-commands';
    import * as History from 'prosemirror-history';    

    // TODO: would prefer not to include toastr for errors. Maybe we can
    // integrate errors into the toolbar somehow, or emit them as events for the
    // using class to handle?
    import toastr from 'toastr';

    import EmojiPicker from '../emoji-picker';
    import EmojiUtils from 'src/utils/emoji';
    import Noembed from 'src/services/noembed';
    import StringUtils from 'src/utils/strings';


    function isMac() {
        if (typeof navigator != 'undefined') {
            return /Mac/.test(navigator.platform);
        }
        return false;
    }

    function buildSchema(tagMap) {
        let nodes = BasicSchema.schema.spec.nodes;
        nodes = ListSchema.addListNodes(nodes, "paragraph block*", "block");
        nodes = nodes.remove('blockquote');
        nodes = nodes.remove('code_block');
        nodes = nodes.remove('heading');
        nodes = nodes.remove('horizontal_rule');
        nodes = nodes.remove('image');

        nodes = nodes.addToEnd('image', {
            attrs: {
                src: {},
                alt: { default: null },
                title: { default: null },
                width: { default: null },
                height: { default: null },
            },
            draggable: true,
            group: 'block media',
            parseDOM: [{
                tag: 'img[src]',
                getAttrs(dom) {
                    return {
                        src: dom.getAttribute('src'),
                        alt: dom.getAttribute('alt'),
                        title: dom.getAttribute('title'),
                        width: dom.getAttribute('width'),
                        height: dom.getAttribute('height'),
                    };
                },
            }],
            toDOM(node) {
                return ['img', node.attrs];
            },
        });
        nodes = nodes.addToEnd('video', {
            attrs: {
                src: {},
                alt: { default: null },
                title: { default: null },
                width: { default: null },
                height: { default: null },
                controls: { default: true },
            },
            group: 'block media',
            parseDOM: [{
                tag: 'video[src]',
                getAttrs(dom) {
                    return {
                        src: dom.getAttribute('src'),
                        alt: dom.getAttribute('alt'),
                        title: dom.getAttribute('title'),
                        width: dom.getAttribute('width'),
                        height: dom.getAttribute('height'),
                        controls: dom.getAttribute('controls'),
                    };
                },
            }],
            toDOM(node) {
                return ['video', node.attrs];
            },
        });
        nodes = nodes.addToEnd('iframe_video', {
            attrs: {
                src: {},
                width: { default: null },
                height: { default: null },
                allow: { default: null },
                allowfullscreen: { default: null },
            },
            group: 'media',
            parseDOM: [{
                tag: 'iframe[src]',
                getAttrs(dom) {
                    return {
                        src: dom.getAttribute('src'),
                        width: dom.getAttribute('width'),
                        height: dom.getAttribute('height'),
                        allow: dom.getAttribute('allow'),
                        allowfullscreen: dom.getAttribute('allowfullscreen'),
                    };
                },
            }],
            toDOM(node) {
                const w = parseInt(node.attrs.width);
                const h = parseInt(node.attrs.height);
                const p = `${h / w * 100}%`;
                return [
                    'div',
                    { class: 'iframe-wrapper' },
                    ['div', {
                        class: 'iframe-spacer',
                        style: `padding-top: ${p}`
                    }],
                    ['iframe', node.attrs],
                ];
            },
        });
        nodes = nodes.addToEnd('figcaption', {
            content: 'inline*',
            isolating: true,
            parseDOM: [{ tag: 'figcaption' }],
            toDOM(node) {
                function isEmpty() {
                    if (node.textContent.trim().length > 0) {
                        return false;
                    }
                    let hasContent = false;
                    node.descendants((childNode) => {
                        if (childNode.type.name === 'emoji') {
                            hasContent = true;
                        }
                    });
                    return !hasContent;
                }
                if (isEmpty()) {
                    return ['figcaption', { class: 'empty' }];
                } else {
                    return ['figcaption', 0];
                }
            },
        });
        nodes = nodes.addToEnd('figure', {
            // Media needs to be optional, since it's not default-constructable,
            // so if the editor runs into a case where the figure doesn't have
            // any media, it must be able to handle it.
            content: 'media? figcaption',
            group: 'block',
            isolating: true,
            parseDOM: [{ tag: 'figure', }],
            toDOM() { return ['figure', 0] },
        });
        nodes = nodes.addToEnd('heading1', {
            content: 'inline*',
            group: 'block',
            defining: true,
            parseDOM: [{ tag: tagMap.h1 || 'h1' }],
            toDOM() { return [tagMap.h1 || 'h1', 0] },
        });
        nodes = nodes.addToEnd('heading2', {
            content: 'inline*',
            group: 'block',
            defining: true,
            parseDOM: [{ tag: tagMap.h2 || 'h2' }],
            toDOM() { return [tagMap.h2 || 'h2', 0] },
        });
        nodes = nodes.addBefore('image', 'emoji', {
            attrs: {
                unicode: {},
            },
            inline: true,
            group: 'inline',
            parseDOM: [{
                tag: 'img[data-emoji]',
                getAttrs(dom) {
                    return {
                        unicode: dom.getAttribute('data-emoji'),
                    };
                },
            }],
            toDOM(node) {
                const unicode = node.attrs.unicode;
                const emoji = EmojiUtils.getEmojiByUnicode(unicode);
                return ['img', {
                    'data-emoji': unicode,
                    alt: unicode,
                    src: emoji ? emoji.url : null,
                    'class': 'emoji',
                }];
            },
        });

        let marks = BasicSchema.schema.spec.marks;
        marks = marks.update('link', {
            // The default link doesn't allow for the "target" attribute, so we
            // have to replace the mark.
            attrs: {
                href: {},
                title: { default: null },
                target: { default: null },
            },
            inclusive: false,
            parseDOM: [{
                tag: 'a[href]',
                getAttrs(dom) {
                    return {
                        href: dom.getAttribute('href'),
                        title: dom.getAttribute('title'),
                        target: dom.getAttribute('target'),
                    };
                },
            }],
            toDOM(node) {
                return ['a', {
                    href: node.attrs.href,
                    title: node.attrs.title,
                    target: node.attrs.target,
                }];
            },
        });

        return new Model.Schema({ nodes, marks });
    }

    class FigcaptionView {
        constructor(node) {
            this.dom = document.createElement('figcaption');
            this.contentDOM = this.dom;
            this._updateEmpty(node);
        }

        _updateEmpty(node) {
            // NOTE: this logic is different from the toDOM() method of the
            // figcaption node. While editing, the placeholder text should
            // disappear immediately after typing, even if it's whitespace. Not
            // doing this makes it feel broken/unresponsive. While serializing
            // during save, however, we can apply our additional logic that
            // trims the caption before checking, etc.
            function isEmpty() {
                return node.content.size === 0;
            }
            if (isEmpty()) {
                this.dom.classList.add('empty');
            } else {
                this.dom.classList.remove('empty');
            }
        }

        update(node) {
            if (node.type.name !== 'figcaption') {
                return false;
            }
            this._updateEmpty(node);
            return true;
        }
    }

    function buildInputRules(schema) {
        // Taken from:
        // https://github.com/ProseMirror/prosemirror-example-setup/blob/master/src/inputrules.js

        // Add rules to auto-convert quotes, ellipses, and em dashes into their
        // correct characters.
        let rules = InputRules.smartQuotes.concat(
            InputRules.ellipsis,
            InputRules.emDash,
        );

        // Auto-start numbered lists
        const numListRule = InputRules.wrappingInputRule(
            /^(\d+)\.\s$/,
            schema.nodes.ordered_list,
            match => ({ order: +match[1] }),
            (match, node) => node.childCount + node.attrs.order == +match[1]
        );
        rules.push(numListRule);

        // Auto-start unordered lists
        const bulListRule = InputRules.wrappingInputRule(
            /^\s*([-+*])\s$/,
            schema.nodes.bullet_list,
        );
        rules.push(bulListRule);

        // Auto-start headings with markdown-style #
        const h1Rule = InputRules.textblockTypeInputRule(
            /^#\s$/,
            schema.nodes.heading1,
        );
        const h2Rule = InputRules.textblockTypeInputRule(
            /^##\s$/,
            schema.nodes.heading2,
        );
        rules.push(h1Rule);
        rules.push(h2Rule);

        return InputRules.inputRules({ rules });
    }

    function buildKeymap(schema) {
        // Taken from:
        // https://github.com/ProseMirror/prosemirror-example-setup/blob/master/src/keymap.js
        const keys = {};
        function bind(key, cmd) {
            keys[key] = cmd;
        }

        const n = schema.nodes;
        const m = schema.marks;

        bind('Escape', Commands.chainCommands);

        bind('Mod-z', History.undo);
        bind('Shift-Mod-z', History.redo);
        bind('Mod-y', History.redo);

        bind('Mod-b', Commands.toggleMark(m.strong));
        bind('Mod-i', Commands.toggleMark(m.em));

        {
            const cmd = Commands.chainCommands(
                Commands.exitCode,
                (state, dispatch) => {
                    const br = n.hard_break.create();
                    dispatch(
                        state.tr.replaceSelectionWith(br).scrollIntoView()
                    );
                    return true;
                }
            );
            bind('Mod-Enter', cmd);
            bind('Shift-Enter', cmd);
            if (isMac()) {
                bind('Ctrl-Enter', cmd);
            }
        }

        bind('Mod-[', ListSchema.liftListItem(n.list_item));
        bind('Mod-]', ListSchema.sinkListItem(n.list_item));

        bind('Enter', (state, dispatch) => {
            // Try: create new list ites
            const listCmd = ListSchema.splitListItem(n.list_item);
            if (listCmd(state, dispatch)) {
                return true;
            }

            // Try: insert new paragraphs above or below figures
            const figureCmd = (state, dispatch) => {
                function insertP(pos) {
                    if (dispatch) {
                        const tr = state.tr;
                        tr.insert(pos, schema.node('paragraph'));
                        tr.setSelection(
                            State.TextSelection.near(tr.doc.resolve(pos))
                        );
                        dispatch(tr);
                    }
                }
                const sel = state.selection;
                if (sel.$from.parent.type === n.figure) {
                    // Selection is on the media, so insert p above
                    const pos = sel.$from.before();
                    insertP(pos);
                    return true;

                } else if (sel.$from.parent.type === n.figcaption &&
                           sel.$from.parentOffset === 0) {
                    // Selection is at start of caption, so insert p above
                    const pos = sel.$from.before(sel.$from.depth - 1);
                    insertP(pos);
                    return true;

                } else if (sel.$from.parent.type === n.figcaption) {
                    // Selection is somewhere in caption, so insert p below
                    const pos = sel.$from.after(sel.$from.depth - 1);
                    insertP(pos);
                    return true;

                } else {
                    return false;
                }
            };
            return figureCmd(state, dispatch);
        });

        bind('Backspace', (state, dispatch) => {
            // Try: undo last input
            if (InputRules.undoInputRule(state, dispatch)) {
                return true;
            }

            // Try: delete figure
            const figureCmd = (state, dispatch) => {
                function deleteFig(p1, p2) {
                    if (dispatch) {
                        const tr = state.tr;
                        tr.delete(p1, p2);
                        dispatch(tr);
                    }
                }
                const sel = state.selection;
                if (sel.$from.parent.type === n.figure) {
                    // Selection is on the media, so remove the figure
                    const p1 = sel.$from.before();
                    const p2 = sel.$from.after();
                    deleteFig(p1, p2);
                    return true;

                } else if (sel.$from.parent.type === n.figcaption &&
                           sel.$from.parentOffset === 0 &&
                           sel.from === sel.to) {
                    // Selection is at start of caption, and isn't highlighting
                    // anything, so remove the figure
                    const p1 = sel.$from.before(sel.$from.depth - 1);
                    const p2 = sel.$from.after(sel.$from.depth - 1);
                    deleteFig(p1, p2);
                    return true;

                } else {
                    return false;
                }
            };
            return figureCmd(state, dispatch);
        });

        return keys;
    }

    // Add a placeholder
    function buildPlaceholderPlugin() {
          return new State.Plugin({
            props: {
              decorations: state => {
                const decorations = []

                const decorate = (node, pos) => {
                  if (node.type.isBlock && node.childCount === 0) {
                    decorations.push(
                      View.Decoration.node(pos, pos + node.nodeSize, {
                        class: 'empty-node',
                      })
                    )
                  }
                }

                state.doc.descendants(decorate)

                return View.DecorationSet.create(state.doc, decorations)
              },
            },
          })
    }


    /**
     * TODO: document what this plugin does. I *believe* what it does is simply
     * watch cursor changes and detect if the highlighted text is bolded, or if
     * we're in a list, etc. so we can highlight the menu buttons.
     */
    function buildMenuPlugin(schema, config) {
        const n = schema.nodes;
        const m = schema.marks;

        function update(view) {
            const state = view.state;
            function checkCommand(cmd) {
                return cmd(view.state, null, view);
            }
            function getSelectionNodeType() {
                const selection = state.selection;
                if (selection.empty) {
                    return selection.$cursor.parent.type;
                } else {
                    let types = [];
                    selection.content().content.forEach(node => {
                        if (types.length > 1) {
                            return;
                        }
                        if (types.length === 0) {
                            types.push(node.type);
                            return;
                        }
                        if (types[0] !== node.type) {
                            types.push(node.type);
                        }
                    });
                    if (types.length > 1) {
                        return null;
                    } else {
                        return types[0];
                    }
                }
            }
            function selectionInOl() {
                const liftCmd = ListSchema.liftListItem(n.list_item);
                if (!checkCommand(liftCmd)) {
                    return false;
                }

                const selection = state.selection;
                if (selection.empty) {
                    let depth = selection.$cursor.depth;
                    let node;
                    while (node = selection.$cursor.node(depth)) {
                        if (node.type === n.ordered_list) {
                            return true;
                        }
                        if (node.type === n.bullet_list) {
                            return false;
                        }
                        depth -= 1;
                    }
                    return false;
                } else {
                    const content = selection.content().content;
                    const firstNode = content.firstChild;
                    return firstNode.type === n.ordered_list;
                }
            }
            function selectionInUl() {
                const liftCmd = ListSchema.liftListItem(n.list_item);
                if (!checkCommand(liftCmd)) {
                    return false;
                }

                const selection = state.selection;
                if (selection.empty) {
                    let depth = selection.$cursor.depth;
                    let node;
                    while (node = selection.$cursor.node(depth)) {
                        if (node.type === n.bullet_list) {
                            return true;
                        }
                        if (node.type === n.ordered_list) {
                            return false;
                        }
                        depth -= 1;
                    }
                    return false;
                } else {
                    const content = selection.content().content;
                    const firstNode = content.firstChild;
                    return firstNode.type === n.bullet_list;
                }
            }

            const c = config;
            if (c.setSelectionStrong) {
                c.setSelectionStrong(isMarkActive(state, m.strong));
            }
            if (c.setSelectionEm) {
                c.setSelectionEm(isMarkActive(state, m.em));
            }
            if (c.setSelectionLink) {
                c.setSelectionLink(isMarkActive(state, m.link));
            }
            if (c.setSelectionNodeType) {
                c.setSelectionNodeType(getSelectionNodeType());
            }
            if (c.setSelectionInOl) {
                c.setSelectionInOl(selectionInOl());
            }
            if (c.setSelectionInUl) {
                c.setSelectionInUl(selectionInUl());
            }
        }

        return new State.Plugin({
            view() {
                return { update };
            }
        });
    }

    /**
     * This plugin watches for base64-encoded images within pasted content and
     * replaces them with Object URLs. This way they can be handled like any
     * other image uploaded by file.
     */
    function buildBase64ImagePlugin(htmlEditor) {
        function transformImage(node) {
            if (!node.attrs.src.startsWith('data:')) {
                return node;
            }

            const urlParts = StringUtils.parseDataUrl(node.attrs.src);
            const object = StringUtils.base64ToBlob(
                urlParts.data,
                urlParts.contentType,
            );
            const objectUrl = URL.createObjectURL(object);
            htmlEditor.contentFiles.push({ object, objectUrl, kind: 'image' });

            const newAttrs = Object.assign({}, node.attrs, { src: objectUrl });
            const newMarks = Model.Mark.setFrom(node.marks);
            node = node.type.create(newAttrs, null, newMarks);
            return node;
        }

        function transformFragment(fragment) {
            let newNodes = [];
            fragment.forEach(node => {
                if (node.type.name === 'image') {
                    node = transformImage(node);
                } else {
                    node = node.copy(transformFragment(node.content));
                }
                newNodes.push(node);
            });
            return Model.Fragment.fromArray(newNodes);
        }

        function transformPasted(slice) {
            return new Model.Slice(
                transformFragment(slice.content),
                slice.openStart,
                slice.openEnd,
            );
        }

        return new State.Plugin({
            props: { transformPasted },
        });
    }

    /**
     * This plugin watches for load events that are generated by images and
     * videos so that we can save the resolution on the element in the `width`
     * and `height` attributes.
     *
     * These attributes are useful to have so that the browser can reserve space
     * for them before they're loaded, and prevent a bunch of reflow jank once
     * they do load in.
     *
     * If we only needed to allow uploads by file, then we could probably pull
     * these from the File object itself. However, if a user creates an image or
     * video by link, then we have no choice but to do this asynchronously.
     *
     * TODO: would it be cleaner to use NodeViews for this?
     */
    function buildAutoMediaSizePlugin(htmlEditor) {
        let state = null;
        let view = null;

        function updateSize(type, src, width, height) {

            state.doc.descendants((node, pos) => {
                if (node.type.name !== type ||
                    node.attrs.src !== src ||
                    node.attrs.width !== null ||
                    node.attrs.height !== null) {
                    return;
                }

                const attrs = Object.assign({}, node.attrs, { width, height });
                const newNode = state.schema.node(node.type, attrs);

                const tr = state.tr;
                tr.replaceWith(pos, pos + node.nodeSize, newNode);
                tr.setMeta('addToHistory', false);
                view.dispatch(tr);
            });
        }

        function onImageLoad(event) {
            // TODO: event.path is Chrome-only
            if (!$(event.path[0]).is('figure > img')) {
                return;
            }

            const src = event.path[0].getAttribute('src');
            const width = event.path[0].naturalWidth;
            const height = event.path[0].naturalHeight;

            updateSize('image', src, width, height);
        }

        function onVideoLoad(event) {
            // TODO: event.path is Chrome-only
            if (!$(event.path[0]).is('figure > video')) {
                return;
            }

            const src = event.path[0].getAttribute('src');
            const width = event.path[0].videoWidth;
            const height = event.path[0].videoHeight;

            updateSize('video', src, width, height);
        }

        return new State.Plugin({
            view() {
                const root = htmlEditor.$el;
                const capture = { capture: true };

                function on(name, func, opt) {
                    root.addEventListener(name, func, opt);
                }
                function off(name, func, opt) {
                    root.removeEventListener(name, func, opt);
                }

                on('load', onImageLoad, capture);
                on('loadedmetadata', onVideoLoad, capture);

                return {
                    update(_view) {
                        view = _view;
                        state = view.state;
                    },
                    destroy() {
                        off('load', onImageLoad, capture);
                        off('loadedmetadata', onVideoLoad, capture);
                    },
                };
            },
        });
    }

    function isMarkActive(state, type) {
        let {from, $from, to, empty} = state.selection;
        if (empty) {
            return type.isInSet(state.storedMarks || $from.marks());
        } else {
            return state.doc.rangeHasMark(from, to, type);
        }
    }

    function checkFileTypeAgainstList(typeList, file) {
        for (let type of typeList) {
            type = type.trim();
            if (type.startsWith('.')) {
                if (file.name.endsWith(type)) {
                    return true;
                }
            } else {
                if (file.type === type) {
                    return true;
                }
            }
        }
        return false;
    }

    function validateFileByWhitelist(whitelist, file) {
        return checkFileTypeAgainstList(whitelist, file);
    }

    function validateFileByBlacklist(blacklist, file) {
        return !checkFileTypeAgainstList(blacklist, file);
    }


    export default {
        name: 'html-editor',

        props: {
            // TODO: why don't any of these have types?
            tagMap: {
                // The tag map can be used to specify alternative tag names to
                // use for editor elements. For example, using { h1: 'h3' } will
                // use the 'h3' tag instead of 'h1' when the 'heading1' style is
                // applied.
                // Only `h1` and `h2` keys are currently supported. Edit
                // `buildSchema()` to add support for more.
                default: () => ({}),
            },
            contentClass: {
                default: '',
            },
            stickyToolbar: {
                // Currently, this prop is not dynamic (you should not change it
                // after component has been mounted).
                default: false,
            },
            allowEmoji: {
                default: false,
            },

            // Types can be either mime type or an extension.
            //
            // TODO: add support for wildcards, like "image/*".

            allowedImageTypes: {
                default: () => [
                    'image/apng',
                    'image/bmp',
                    'image/gif',
                    'image/jpeg',
                    'image/png',
                    'image/webp',

                    // Security vulnerability should be reviewed first. See:
                    //   https://github.com/w3c/svgwg/issues/266
                    //'image/svg+xml',
                ],
            },
            allowedVideoTypes: {
                default: () => [
                    'video/mp4',
                    'video/ogg',
                    'video/webm',
                ],
            },
            disallowedFileTypes: {
                default: () => [
                    '.ade', '.adp', '.apk', '.appx', '.appxbundle', '.bat',
                    '.cab', '.chm', '.cmd', '.com', '.cpl', '.dll', '.dmg',
                    '.exe', '.hta', '.ins', '.isp', '.iso', '.jar', '.js',
                    '.jse', '.lib', '.lnk', '.mde', '.msc', '.msi', '.msix',
                    '.msixbundle', '.msp', '.mst', '.nsh', '.pif', '.ps1',
                    '.scr', '.sct', '.shb', '.sys', '.vb', '.vbe', '.vbs',
                    '.vxd', '.wsc', '.wsf', '.wsh',

                    // List taken from Gmail. See:
                    //   https://support.google.com/mail/answer/6590?hl=en
                ],
            },
        },

        components: {
            EmojiPicker,
        },

        data() {
            return {
                style: 'normal',
                isSelectionBold: false,
                isSelectionItalic: false,
                isSelectionLink: false,
                isSelectionOl: false,
                isSelectionUl: false,
                lastSelection: null,
                menuEnabled: null, // 'link' | 'image' | 'video' | 'file' | 'emoji' | null
                linkMenuUrl: null,
                linkMenuText: null,
                imageMenuMode: 'file',
                imageMenuFiles: [],
                imageMenuUrl: null,
                imageMenuCaption: null,
                videoMenuMode: 'url',
                videoMenuFiles: [],
                videoMenuUrl: null,
                videoMenuUrlInfo: null,
                videoMenuCaption: null,
                fileMenuFiles: [],

                view: null,
                schema: null,
                contentFiles: [],
                stickyToolbarTimer: null,
            };
        },

        mounted() {
            this.schema = buildSchema(this.tagMap);
            const state = State.EditorState.create({
                schema: this.schema,
                plugins: [                    
                    buildInputRules(this.schema),
                    Keymap.keymap(buildKeymap(this.schema)),
                    Keymap.keymap(Commands.baseKeymap),
                    this.buildMenuPlugin(this.schema),
                    this.buildBase64ImagePlugin(),
                    this.buildAutoMediaSizePlugin(),
                    this.buildPlaceholderPlugin(),
                    History.history(),
                ],
            });
            this.editor = new View.EditorView(this.$el, {
                state,
                nodeViews: {
                    figcaption(node) {
                        return new FigcaptionView(node);
                    },
                },
            });

            const editor = this.editor;
            editor.dom.classList.add('html-content');
            editor.dom.className = editor.dom.className + ' ' + this.contentClass;

            window.addEventListener('keyup', this.onKeyUp);

            if (this.stickyToolbar) {
                this.stickyToolbarTimer = setInterval(
                    this.updateStickyToolbar,
                    1000,
                );
                window.addEventListener('scroll', this.updateStickyToolbar);
            }
        },

        beforeDestroy() {
            for (const contentFile of this.contentFiles) {
                URL.revokeObjectURL(contentFile.objectUrl);
            }
            this.contentFiles = [];

            window.removeEventListener('keyup', this.onKeyUp);

            if (this.stickyToolbar) {
                clearInterval(this.stickyToolbarTimer);
                this.stickyToolbarTimer = null;
                window.removeEventListener('scroll', this.updateStickyToolbar);
            }
        },

        methods: {
            onKeyUp(ev) {
                if (ev.key === 'Escape') {
                    if (this.menuEnabled) {
                        this.menuEnabled = null;
                        ev.preventDefault();
                    }
                }
            },

            focus() {
                this.editor.focus();
            },

            buildMenuPlugin(schema) {
                return buildMenuPlugin(schema, {
                    setSelectionStrong: flag => {
                        this.isSelectionBold = flag;
                    },
                    setSelectionEm: flag => {
                        this.isSelectionItalic = flag;
                    },
                    setSelectionLink: flag => {
                        this.isSelectionLink = flag;
                    },
                    setSelectionNodeType: type => {
                        if (!type) {
                            this.style = null;
                            return;
                        }
                        switch (type.name) {
                            case 'heading1':
                                this.style = 'h1';
                                break;

                            case 'heading2':
                                this.style = 'h2';
                                break;

                            default:
                                this.style = 'normal';
                                break;
                        }
                    },
                    setSelectionInOl: flag => {
                        this.isSelectionOl = flag;
                    },
                    setSelectionInUl: flag => {
                        this.isSelectionUl = flag;
                    },
                });
            },

            buildBase64ImagePlugin() {
                return buildBase64ImagePlugin(this);
            },

            buildAutoMediaSizePlugin() {
                return buildAutoMediaSizePlugin(this);
            },
            buildPlaceholderPlugin() {
                return buildPlaceholderPlugin(this);
            },
            runCommand(name, data) {
                const n = this.schema.nodes;
                const m = this.schema.marks;
                const doCommand = cmd => {
                    return cmd(
                        this.editor.state,
                        this.editor.dispatch,
                        this.editor,
                    );
                };
                const checkCommand = cmd => {
                    return cmd(
                        this.editor.state, null,
                        this.editor,
                    );
                };

                this.editor.focus();

                switch (name) {
                    case 'create-emoji':
                        {
                            if (!data || !data.unicode) {
                                throw new Error('unicode required for emoji');
                            }

                            const selection = this.editor.state.selection;
                            const tr = this.editor.state.tr;
                            const node = this.schema.node('emoji', data);
                            tr.replaceSelectionWith(node);
                            return this.editor.dispatch(tr);
                        }

                    case 'create-file':
                        {
                            data = data || {};
                            data.src = data.src || '';
                            data.name = data.name || data.src;

                            const tr = this.editor.state.tr;
                            if (data.forceNewLine) {
                                const br = this.schema.nodes.hard_break.create();
                                tr.replaceSelectionWith(br);
                            }

                            const mark = this.schema.mark('link', {
                                href: data.src,
                                target: '_blank',
                            });
                            tr.ensureMarks([mark]);
                            tr.insertText(data.name);
                            return this.editor.dispatch(tr);
                        }

                    case 'create-image':
                        {
                            data = data || {};
                            data.src = data.src || '';
                            data.caption = data.caption || '';

                            const imgNode = this.schema.node(
                                'image',
                                { src: data.src },
                            );
                            const capNode = this.schema.node(
                                'figcaption',
                                null,
                                data.caption
                                    ? this.schema.text(data.caption)
                                    : null,
                            );
                            const figNode = this.schema.node(
                                'figure',
                                null,
                                [imgNode, capNode],
                            );

                            const state = this.editor.state;
                            const tr = state.tr;
                            const pos = state.selection.$from.before();
                            tr.insert(pos, figNode);
                            return this.editor.dispatch(tr);
                        }

                    case 'create-link':
                        if (!this.isSelectionLink) {
                            data = data || {};
                            data.href = data.href || '';
                            data.text = data.text || data.href;

                            const selection = this.editor.state.selection;
                            if (selection.empty) {
                                const tr = this.editor.state.tr;
                                const mark = this.schema.mark('link', {
                                    href: data.href,
                                });
                                tr.ensureMarks([mark]);
                                tr.insertText(data.text);
                                return this.editor.dispatch(tr);

                            } else {
                                const cmd = Commands.toggleMark(m.link, {
                                    href: data.href,
                                });
                                return doCommand(cmd);
                            }
                        }
                        return false;

                    case 'create-video':
                        {
                            data = data || {};
                            data.src = data.src || '';
                            data.caption = data.caption || '';

                            let videoNode;
                            if (data.iframe) {
                                videoNode = this.schema.node(
                                    'iframe_video',
                                    {
                                        width: data.iframe.width,
                                        height: data.iframe.height,
                                        src: data.iframe.src,
                                        allowfullscreen: true,
                                    },
                                );
                            } else {
                                videoNode = this.schema.node(
                                    'video',
                                    {
                                        src: data.src,
                                        controls: true,
                                    },
                                );
                            }
                            const capNode = this.schema.node(
                                'figcaption',
                                null,
                                data.caption
                                    ? this.schema.text(data.caption)
                                    : null,
                            );
                            const figNode = this.schema.node(
                                'figure',
                                null,
                                [videoNode, capNode],
                            );

                            const state = this.editor.state;
                            const tr = state.tr;
                            const pos = state.selection.$from.before();
                            tr.insert(pos, figNode);
                            return this.editor.dispatch(tr);
                        }

                    case 'remove-link':
                        if (this.isSelectionLink) {
                            return doCommand(Commands.toggleMark(m.link));
                        }
                        return false;

                    case 'set-style':
                        if (data === 'h1') {
                            return doCommand(Commands.setBlockType(n.heading1));
                        }
                        if (data === 'h2') {
                            return doCommand(Commands.setBlockType(n.heading2));
                        }
                        return doCommand(Commands.setBlockType(n.paragraph));

                    case 'toggle-bold':
                        return doCommand(Commands.toggleMark(m.strong));

                    case 'toggle-italic':
                        return doCommand(Commands.toggleMark(m.em));

                    case 'toggle-ol':
                        {
                            const liftCmd = ListSchema.liftListItem(n.list_item);
                            if (checkCommand(liftCmd) && this.isSelectionOl) {
                                return doCommand(liftCmd);
                            }
                            let wrapCmd = ListSchema.wrapInList(n.ordered_list);
                            if (checkCommand(wrapCmd)) {
                                return doCommand(wrapCmd);
                            }
                            wrapCmd = Commands.wrapIn(n.ordered_list);
                            return doCommand(wrapCmd);
                        }

                    case 'toggle-ul':
                        {
                            const liftCmd = ListSchema.liftListItem(n.list_item);
                            if (checkCommand(liftCmd) && this.isSelectionUl) {
                                return doCommand(liftCmd);
                            }
                            let wrapCmd = ListSchema.wrapInList(n.bullet_list);
                            if (checkCommand(wrapCmd)) {
                                return doCommand(wrapCmd);
                            }
                            wrapCmd = Commands.wrapIn(n.bullet_list);
                            return doCommand(wrapCmd);
                        }

                    default:
                        throw new Error('unknown command:', name);
                }
            },

            setStyle(style) {
                const resetStyle = () => {
                    const temp = this.style;
                    this.style = null;
                    this.style = temp;
                };

                switch (style) {
                    case 'h1':
                        if (this.runCommand('set-style', 'h1')) {
                            this.style = style;
                        } else {
                            resetStyle();
                        }
                        break;

                    case 'h2':
                        if (this.runCommand('set-style', 'h2')) {
                            this.style = style;
                        } else {
                            resetStyle();
                        }
                        break;

                    default:
                        if (this.runCommand('set-style', 'normal')) {
                            this.style = style;
                        } else {
                            resetStyle();
                        }
                        break;
                }
            },

            createLink() {
                this.runCommand('create-link', {
                    href: this.linkMenuUrl,
                    text: this.linkMenuText,
                });
                this.toggleLinkMenu();
            },

            removeLink() {
                this.runCommand('remove-link');
            },

            createImage() {
                if (this.imageMenuMode === 'file') {

                    // Validate against whitelist
                    for (const file of this.imageMenuFiles) {
                        const fileIsAllowed = validateFileByWhitelist(
                            this.allowedImageTypes,
                            file,
                        );
                        if (!fileIsAllowed) {
                            toastr.error(`The file "${file.name}" is not allowed as an image. Perhaps try uploading it as a file instead.`);
                            return;
                        }
                    }

                    // Create the images
                    for (const file of this.imageMenuFiles) {
                        const contentFile = {
                            object: file,
                            objectUrl: URL.createObjectURL(file),
                            kind: 'image',
                        };
                        this.contentFiles.push(contentFile);
                        this.runCommand('create-image', {
                            src: contentFile.objectUrl,
                            caption: this.imageMenuCaption,
                        });
                    }

                } else {
                    this.runCommand('create-image', {
                        src: this.imageMenuUrl,
                        caption: this.imageMenuCaption,
                    });

                    // TODO: the video menu forces the client to check the video
                    // URL and ensure it's from YouTube or Vimeo. Perhaps we
                    // should add a similar check for the image menu to verify
                    // that the image URL is actually an image?
                }

                this.toggleImageMenu();
            },

            createVideo() {
                if (this.videoMenuMode === 'file') {

                    // Validate against whitelist
                    for (const file of this.videoMenuFiles) {
                        const fileIsAllowed = validateFileByWhitelist(
                            this.allowedVideoTypes,
                            file,
                        );
                        if (!fileIsAllowed) {
                            toastr.error(`The file "${file.name}" is not allowed as a video. Perhaps try uploading it as a file instead.`);
                            return;
                        }
                    }

                    // Create the videos
                    for (const file of this.videoMenuFiles) {
                        const contentFile = {
                            object: file,
                            objectUrl: URL.createObjectURL(file),
                            kind: 'video',
                        };
                        this.contentFiles.push(contentFile);
                        this.runCommand('create-video', {
                            src: contentFile.objectUrl,
                            caption: this.videoMenuCaption,
                        });
                    }

                } else {
                    const iframe = this.videoMenuUrlInfo;
                    iframe.src = $(this.videoMenuUrlInfo.html).attr('src');
                    this.runCommand('create-video', {
                        src: this.videoMenuUrl,
                        iframe,
                        caption: this.videoMenuCaption,
                    });
                }

                this.toggleVideoMenu();
            },

            createFiles() {
                // Validate against blacklist
                for (const file of this.fileMenuFiles) {
                    const fileIsAllowed = validateFileByBlacklist(
                        this.disallowedFileTypes,
                        file,
                    );
                    if (!fileIsAllowed) {
                        toastr.error(`The file "${file.name}" is not allowed.`);
                        return;
                    }
                }

                // Create the files
                for (const i in this.fileMenuFiles) {
                    const file = this.fileMenuFiles[i];
                    const contentFile = {
                        object: file,
                        objectUrl: URL.createObjectURL(file),
                        kind: 'file',
                    };
                    this.contentFiles.push(contentFile);
                    this.runCommand('create-file', {
                        src: contentFile.objectUrl,
                        name: file.name,
                        forceNewLine: i > 0,
                    });
                }
                this.toggleFileMenu();
            },

            createEmoji(emojiValue) {
                this.runCommand('create-emoji', {
                    unicode: emojiValue,
                });
                // Disabled for UX: some people like to spam emoji
                //this.toggleEmojiMenu();
            },

            toggleLinkMenu() {
                if (this.menuEnabled !== 'link') {
                    this.menuEnabled = 'link';
                } else {
                    this.menuEnabled = null;
                }

                this.linkMenuUrl = null;
                this.linkMenuText = null;

                if (!this.linkMenuEnabled) {
                    this.editor.focus();
                }
            },

            toggleImageMenu() {
                if (this.menuEnabled !== 'image') {
                    this.menuEnabled = 'image';
                } else {
                    this.menuEnabled = null;
                }

                this.imageMenuFiles = [];
                this.imageMenuUrl = null;
                this.imageMenuCaption = null;

                if (!this.imageMenuEnabled) {
                    this.editor.focus();
                }
            },

            toggleVideoMenu() {
                if (this.menuEnabled !== 'video') {
                    this.menuEnabled = 'video';
                } else {
                    this.menuEnabled = null;
                }

                this.videoMenuFiles = [];
                this.videoMenuUrl = null;
                this.videoMenuCaption = null;

                if (!this.videoMenuEnabled) {
                    this.editor.focus();
                }
            },

            toggleFileMenu() {
                if (this.menuEnabled !== 'file') {
                    this.menuEnabled = 'file';
                } else {
                    this.menuEnabled = null;
                }

                this.fileMenuFiles = [];

                if (!this.fileMenuEnabled) {
                    this.editor.focus();
                }
            },

            toggleEmojiMenu() {
                if (this.menuEnabled !== 'emoji') {
                    this.menuEnabled = 'emoji';
                } else {
                    this.menuEnabled = null;
                }

                if (!this.emojiMenuEnabled) {
                    this.editor.focus();
                } else {
                    this.$nextTick(() => {
                        this.$refs.emojiPicker.focus();
                    });
                }
            },

            /**
             * Returns editor contents as HTML string.
             */
            getContent() {

                // Convert the editor content to a DocumentFragment
                const writer = Model.DOMSerializer.fromSchema(this.schema);
                const content = this.editor.state.doc.content;
                const frag = writer.serializeFragment(content);

                // Trim empty paragraphs before and after the content
                // TODO: any way we can stick this into a plugin?
                function isEmptyParagraph(node) {
                    const $n = $(node);
                    return (
                        $n.is('p') &&
                        $n.text().trim().length === 0 &&
                        $n.find('.emoji').length === 0
                    );
                }
                function removeNode(node) {
                    if (node.parentNode) {
                        node.parentNode.removeChild(node);
                    }
                }
                while (frag.firstChild && isEmptyParagraph(frag.firstChild)) {
                    removeNode(frag.firstChild);
                }
                while (frag.lastChild && isEmptyParagraph(frag.lastChild)) {
                    removeNode(frag.lastChild);
                }

                // Convert the fragment to html
                const div = document.createElement('div');
                div.appendChild(frag);
                return div.innerHTML;
            },

            /**
             * Returns blobs used within the editor contents.
             *
             * When images, videos, or files are inserted from disk, the files
             * are saved and displayed within the editor using object URLs. When
             * saving the editor contents, you'll want to upload these files to
             * S3 (or some other cloud storage service).
             *
             * The `kind` field is used to differentiate between images, videos,
             * and files. While you _could_ use the `object.type` to figure this
             * out, that wouldn't tell you how the file is rendered. For
             * example, typically images are uploaded and rendered as an
             * `<img>`, and the file should go into a bucket that will generate
             * thumbnails automatically. However, if the image was uploaded as a
             * file and is rendered using `<a>`, then we should treat it as a
             * regular file and we don't need thumbnails.
             *
             * NOTE: it is possible for base64-encoded images to be pasted in
             * the editor, so the `object` field is not always guaranteed to be
             * a `File`.
             *
             * NOTE: you DO NOT have to clean up the object URLs yourself; that
             * will be done when this component is destroyed.
             *
             * @returns {object[]}  files
             * @returns {File|Blob} files[].object
             * @returns {string}    files[].objectUrl
             * @returns {string}    files[].kind - video | image | file
             */
            getContentFiles() {
                return Object.assign([], this.contentFiles);
            },

            /**
             * Sets editor contents from HTML string.
             */
            setContent(html) {
                const p = Model.DOMParser.fromSchema(this.schema);
                const div = document.createElement('div');
                div.innerHTML = html;
                const node = p.parse(div);

                Commands.selectAll(
                    this.editor.state,
                    this.editor.dispatch,
                    this.editor
                );
                const tr = this.editor.state.tr;
                tr.replaceSelectionWith(node);
                this.editor.dispatch(tr);
            },

            /**
             * Updates toolbar CSS to stay at the top of the screen, while also
             * staying within the vertical editor bounds.
             */
            updateStickyToolbar() {
                // ed = editor
                // tb = toolbar
                // vp = viewport
                const topPad = 0; // Padding from top of editor (px)
                const btmPad = 0; // Padding from bottom of editor (px)
                const fltPad = 0; // Padding when floating in editor (px)
                const edEl = this.$el;
                const tbEl = this.$el.querySelector('.html-toolbar');
                const vpTop = window.scrollY;
                const edTop = vpTop + edEl.getBoundingClientRect().top;
                const edBtm = vpTop + edEl.getBoundingClientRect().bottom;
                const tbTop = vpTop + tbEl.getBoundingClientRect().top;
                const tbBtm = vpTop + tbEl.getBoundingClientRect().bottom;
                const tbHgt = tbEl.getBoundingClientRect().height;
                const newTbTop = Math.max(
                    edTop + topPad,
                    Math.min(
                        edBtm - btmPad - tbHgt,
                        vpTop + fltPad,
                    )
                );
                const newTbOff = newTbTop - edTop;
                tbEl.style.top = `${newTbOff}px`;
            },
        },

        computed: {
            linkMenuEnabled() {
                return this.menuEnabled === 'link';
            },

            imageMenuEnabled() {
                return this.menuEnabled === 'image';
            },

            videoMenuEnabled() {
                return this.menuEnabled === 'video';
            },

            fileMenuEnabled() {
                return this.menuEnabled === 'file';
            },

            emojiMenuEnabled() {
                return this.menuEnabled === 'emoji';
            },

            canCreateLink() {
                return !!this.linkMenuUrl;
            },

            canCreateImage() {
                if (this.imageMenuMode === 'file') {
                    return this.imageMenuFiles.length > 0;
                } else {
                    return !!this.imageMenuUrl;
                }
            },

            canCreateVideo() {
                if (this.videoMenuMode === 'file') {
                    return this.videoMenuFiles.length > 0;
                } else {
                    return !!this.videoMenuUrl && !!this.videoMenuUrlInfo;
                }
            },

            canCreateFiles() {
                return this.fileMenuFiles.length > 0;
            },

            linkMenuStatus() {
                if (!this.linkMenuUrl) {
                    return 'URL is required';
                }
                return '';
            },

            imageMenuStatus() {
                if (this.imageMenuMode === 'file') {
                    if (this.imageMenuFiles.length === 0) {
                        return 'File is required';
                    }
                } else {
                    if (!this.imageMenuUrl) {
                        return 'URL is required';
                    }
                }
                return '';
            },

            videoMenuStatus() {
                if (this.videoMenuMode === 'file') {
                    if (this.videoMenuFiles.length === 0) {
                        return 'File is required';
                    }
                } else {
                    if (!this.videoMenuUrl) {
                        return 'URL is required';
                    }
                    if (!this.videoMenuUrlInfo) {
                        return 'URL is invalid';
                    }
                }
                return '';
            },

            fileMenuStatus() {
                if (this.fileMenuFiles.length === 0) {
                    return 'File is required';
                }
                return '';
            },

            imageMenuFileNames() {
                return this.imageMenuFiles.map(f => f.name).join(', ');
            },

            videoMenuFileNames() {
                return this.videoMenuFiles.map(f => f.name).join(', ');
            },

            fileMenuFileNames() {
                return this.fileMenuFiles.map(f => f.name).join(', ');
            },

            isSelectionEmpty() {
                return this.editor.state.selection.empty;
            },

            showToolbarExtension() {
                return this.menuEnabled !== null;
            },
        },

        watch: {
            videoMenuUrl: function(url) {
                this.videoMenuUrlInfo = null;
                Noembed.getInfo(url).then(info => {
                    if (this.videoMenuUrl !== url) {
                        return;
                    }
                    if (!info.providerName.match(/YouTube|Vimeo/i)) {
                        return;
                    }
                    this.videoMenuUrlInfo = info;
                }, () => {});
            },
        },
    }
</script>

<style lang="scss" scoped>
    .html-editor {
        border-bottom: 1px solid #fff;
        position: relative;
    }

    .html-toolbar {
        box-sizing: content-box;
        line-height: 25px;
        background-color: #363636;
        margin-bottom: 10px;
        position: relative;
        z-index: 100;
        &::after {
            content: '';
            display: table;
            clear: both;
        }

        .tool-group {
            float: left;
            margin: 8px 16px 8px 8px;
            height: 25px;
        }

        select {
            background: none;
            border: none;
            display: block;
            height: 100%;
            padding-right: 5px;
            cursor: pointer;
        }

        option {
            background: #363636;
            color: #b0b0b0;
        }

        button {
            background: none;
            border: none;
            padding: 0;
            width: 25px;
            height: 25px;
            font-size: 16px;
            &:hover,
            &.active {
                background-color: #b0b0b0;
                color: #363636;
            }
        }

        .toolbar-extension {
            clear: both;
            border-top: 1px solid #464646;

            input[type="text"] {
                margin: 0 15px 0 0;
                height: 100%;
                line-height: inherit;
                padding: 0;
                border-bottom-color: #b0b0b0 !important;
            }

            input[type="file"] {
                margin: 0 15px 0 0;
                height: 100%;
                line-height: inherit;
                flex: 1;
                min-width: 0;
                padding: 0 0 0 5px;
            }

            input[type="submit"] {
                font-size: 14px;
                height: 100%;
                line-height: inherit;
                padding: 0 20px;
            }

            input[disabled] {
                opacity: 0.5;
            }

            .file-input {
                display: flex;
                min-width: 0;
                top: 0;
                padding: 0 5px 0 0;
                margin: 0 10px 0 0;
                height: 100%;
                white-space: nowrap;
                overflow: hidden;

                input {
                    display: none;
                }

                .action-btn {
                    border: 1px solid #b0b0b0;
                    cursor: pointer;
                    padding: 0 10px;
                    height: 100%;
                    float: left;
                    font-weight: normal !important;
                    &:hover {
                        background-color: #b0b0b0;
                        color: #363636;
                    }
                }

                .filename {
                    flex: 1;
                    margin-left: 5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-width: 0;
                    font-size: 12px;
                }
            }

            .radio-input {
                white-space: nowrap;
                margin-right: 15px;
                height: 100%;
                cursor: pointer;

                label {
                    cursor: inherit;
                    display: block;
                    float: left;
                    margin: 0;
                    padding: 0 10px;
                    height: 100%;
                    line-height: inherit;
                    position: relative;
                    top: 0;
                    border: 1px solid #b0b0b0;
                    &.active {
                        font-weight: normal !important;
                        background-color: #b0b0b0;
                        color: #363636;
                    }

                    input {
                        visibility: hidden;
                        position: absolute;
                        margin: 0;
                    }
                }
            }

            .link-menu,
            .image-menu,
            .video-menu,
            .link-menu {
                display: flex;
                box-sizing: content-box;
                height: 30px;
                line-height: 30px;
                padding: 8px 8px 12px 8px;

                input[type="text"] {
                    flex: 1;
                }
            }

            .image-menu,
            .video-menu,
            .file-menu {
                display: flex;
                box-sizing: content-box;
                height: 30px;
                line-height: 30px;
                padding: 8px 8px 12px 8px;

                input[type="text"],
                .file-input {
                    flex: 1;
                }
            }

            .emoji-picker {
                height: 200px;
                width: 100%;
                border: none;
            }
        }
    }
</style>

<style lang="scss">

        .ProseMirror .empty-node::before {
          position: absolute;
          color: #aaa;
          cursor: text;
        }

        .ProseMirror .empty-node:hover::before {
          color: #777;
        }

        .ProseMirror p.empty-node:first-child::before {
          content: 'Begin typing here…';
        }

    .html-editor {
        .emoji-picker input {
            border-color: #464646;
        }

        .html-content {
            margin: 20px 0;
            outline: none;
            white-space: pre-wrap;
        }

        ul {
            list-style-type: disc;
            padding-left: 40px;
            margin-bottom: 10px;
        }
    }

    .iframe-wrapper {
        position: relative;

        > iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }
    }
</style>



// WEBPACK FOOTER //
// index.vue?acc4c636